import { cellKey, type AttendanceStatus, type GridMark } from '@quire/shared';

/** A cell edited but not yet saved. `null` means "clear the mark that is there". */
export type Draft = Record<string, AttendanceStatus | null>;

/** Saved marks, sparse: an absent key is what "never marked" looks like. */
export type Cells = Record<string, AttendanceStatus>;

/**
 * The draft with everything a completed save accounted for taken out of it.
 *
 * The whole reason this is a function rather than `setDraft({})`: a cell can be
 * marked AGAIN while the request carrying its previous value is still in the
 * air. That second mark has not been sent anywhere, so it has to survive and go
 * out with the next save. Clearing the draft wholesale would drop it silently,
 * and the cell would then show a saved value the server does not hold.
 *
 * So a key is removed only when it still holds exactly what was sent for it.
 * Anything else is a newer edit, and newer edits are never this function's to
 * throw away.
 */
export function settleDraft(draft: Draft, sent: readonly GridMark[]): Draft {
  const next = { ...draft };
  for (const mark of sent) {
    const key = cellKey(mark.studentId, mark.periodId);
    if (key in next && next[key] === mark.status) delete next[key];
  }
  return next;
}

/**
 * The cached cells with a completed save written into them.
 *
 * The register reads the draft first and falls back to these, so taking a key
 * out of the draft without putting its value here makes the cell visibly flick
 * back to whatever it held before, for however long the refetch takes. The two
 * belong in one commit.
 *
 * A cleared mark DELETES its key rather than storing null. `cells` is sparse and
 * an absent key is precisely what "not marked" means; a key holding null would
 * be a third state that nothing else in the app knows how to read.
 */
export function applySaved(cells: Cells, sent: readonly GridMark[]): Cells {
  const next = { ...cells };
  for (const mark of sent) {
    const key = cellKey(mark.studentId, mark.periodId);
    if (mark.status === null) delete next[key];
    else next[key] = mark.status;
  }
  return next;
}
